import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useFinance, usePreferences } from '../../../contexts';
import { getScaleColor } from '../colorScale';
import styles from './ResultadoMensal.module.css';

interface TooltipProps {
    active?: boolean;
    payload?: Array<{ value: number }>;
    label?: string;
    formatCurrency: (value: number) => string;
    labelResultado: string;
}

const CustomTooltip = ({ active, payload, label, formatCurrency, labelResultado }: TooltipProps) => {
    if (active && payload?.length) {
        return (
            <div className={styles.tooltip}>
                <p className={styles.tooltipLabel}>{label}</p>
                <p style={{ color: payload[0].value >= 0 ? '#159243' : '#dc2626' }}>
                    {labelResultado}:{' '}
                    {formatCurrency(payload[0].value)}
                </p>
            </div>
        );
    }
    return null;
};

export function ResultadoMensal() {
    const { cashFlowData } = useFinance();
    const { formatCurrency, t } = usePreferences();

    const data = cashFlowData.map((item) => ({
        month: item.month,
        resultado: item.income - item.expense,
    }));

    const maxAbsValue = Math.max(...data.map(d => Math.abs(d.resultado)), 1);

    const formatYAxis = (value: number) => `${(value / 1000).toFixed(0)}k`;

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>{t('chart.resultadoMensal')}</h3>
            <span className={styles.subtitle}>{t('chart.receitaMinusDespesa')}</span>

            <div className={styles.chartContainer}>
                {data.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: '#9ca3af' }}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={formatYAxis}
                            tick={{ fontSize: 12, fill: '#9ca3af' }}
                        />
                        <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} labelResultado={t('chart.resultado')} />} />
                        <Bar dataKey="resultado" radius={[4, 4, 0, 0]} animationDuration={1500}>
                            {data.map((entry, index) => (
                                <Cell
                                    key={index}
                                    fill={getScaleColor(entry.resultado, maxAbsValue, entry.resultado >= 0 ? 'income' : 'expense')}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
                ) : (
                    <div className={styles.empty}>{t('common.semDados')}</div>
                )}
            </div>
        </div>
    );
}
